/**
 * Picks the default name the Create Scenario modal pre-fills.
 * @module features/squad-planning/utils/nextScenarioName
 */

/** Prefix every auto-generated scenario name carries. */
const SCENARIO_NAME_PREFIX = "Scenario";

/** How many single-letter suffixes exist before the numeric fallback. */
const LETTER_COUNT = 26;

/** Character code of `A`. */
const CODE_A = 65;

/** Scenario shape this module reads — only the name matters. */
interface NamedScenario {
  readonly name?: string | null;
}

/**
 * Builds the set of names already in use, normalized for comparison.
 *
 * The modal rejects duplicates case-insensitively, so the generator has to
 * agree with that comparison or it can hand the user a name that is refused
 * the moment they press Confirm. An unnamed scenario reserves nothing: it
 * cannot collide with anything the duplicate guard compares.
 * @param scenarios - Existing scenarios
 * @returns Lower-cased, trimmed names already taken
 */
function takenNames(scenarios: readonly NamedScenario[]): ReadonlySet<string> {
  return new Set(
    scenarios
      .map(scenario => scenario?.name)
      .filter((name): name is string => typeof name === "string")
      .map(name => name.trim().toLowerCase())
  );
}

/**
 * Builds the ordered list of candidate names: `A`–`Z`, then `27`, `28`, ….
 *
 * The numeric tail is sized to the scenario count, so there are always more
 * candidates than there are names that could occupy them — the search below
 * cannot run out.
 * @param count - How many scenarios already exist
 * @returns Candidate names in preference order
 */
function candidateNames(count: number): readonly string[] {
  const letters = Array.from(
    { length: LETTER_COUNT },
    (_, index) =>
      `${SCENARIO_NAME_PREFIX} ${String.fromCharCode(CODE_A + index)}`
  );
  const numbers = Array.from(
    { length: count + 1 },
    (_, index) => `${SCENARIO_NAME_PREFIX} ${LETTER_COUNT + 1 + index}`
  );
  return [...letters, ...numbers];
}

/**
 * Returns the first unused `Scenario <X>` name.
 *
 * The suffix is the first letter `A`–`Z` no existing scenario already uses,
 * falling back to `Scenario 27`, `Scenario 28`, … once every letter is taken.
 *
 * Why not index by list length (the previous behaviour,
 * `String.fromCharCode(65 + scenarios.length)`): the length is not the same
 * thing as the next free letter. Delete `Scenario B` from `A, B, C` and the
 * length is 2, which regenerates `Scenario C` — a name that already exists, so
 * the modal's own duplicate guard rejects the create. Past 26 scenarios the
 * same arithmetic walks off the end of the alphabet and offers `Scenario [`,
 * `Scenario \`, … which are not names anyone meant to produce.
 *
 * It also made the auto-name a function of unrelated leftovers, which is what
 * broke the Maestro `SE-4559-scenarios` flow: on the shared dev org three
 * stale scenarios turned the "first" scenario into `Scenario D` (SE-7536).
 * @param scenarios - Scenarios that already exist
 * @returns A name no existing scenario uses
 */
export function nextScenarioName(scenarios: readonly NamedScenario[]): string {
  const taken = takenNames(scenarios);
  const free = candidateNames(scenarios.length).find(
    candidate => !taken.has(candidate.toLowerCase())
  );
  // Unreachable: there are more candidates than scenarios, so at least one is
  // always free. Kept as a total return rather than a non-null assertion.
  return (
    free ?? `${SCENARIO_NAME_PREFIX} ${LETTER_COUNT + scenarios.length + 2}`
  );
}
